import Phaser from 'phaser';

import { SCREEN_SIZES } from '@/shared/config/ui';
import { EVENTS } from '@/features/laneChooser/game/EventBus';
import { AnswersController } from '@/features/laneChooser/game/objects/AnswersController';
import { InstructionController } from '@/features/laneChooser/game/objects/InstructionController';
import { PlayerController } from '@/features/laneChooser/game/objects/PlayerController';
import { QuestionController } from '@/features/laneChooser/game/objects/QuestionController';
import { SideElementsController } from '@/features/laneChooser/game/objects/SideElementsController';
import { SkyController } from '@/features/laneChooser/game/objects/SkyController';
import {
	getLaneBottomPct,
	getLaneCameraScrollX,
	getLaneTopPct,
	getLaneWorldScale,
} from '@/features/laneChooser/game/utils/laneLayout';
import { drawLane } from '@/features/laneChooser/game/utils/laneRenderer';
import { createLaneShapeMetrics } from '@/features/laneChooser/game/utils/laneShape';

import type {
	LaneChooserAnswerResolution,
	LaneChooserGameTheme,
	LaneChooserPanelRatios,
	LaneChooserRoundPayload,
	LaneChooserWorldPayload,
} from '@/features/laneChooser/game/types';
import type { LaneChooserEventBus } from '@/features/laneChooser/game/EventBus';
import type { LaneShapeMetrics } from '@/features/laneChooser/game/utils/laneShape';

const MOBILE_LANE_LINE_BOTTOM_WIDTH = 18;
const DESKTOP_LANE_LINE_BOTTOM_WIDTH = 30;
const DEFAULT_LANE_LINES_COUNT = 4;
const PORTRAIT_MOBILE_PANEL_OVERLAP_RATIO = 0.05;

type ResponsiveImageAnchor = 'top' | 'center' | 'bottom';

// Converts hex theme colors into the numeric format expected by Phaser draw APIs.
function getColorValue(color: string): number {
	return Phaser.Display.Color.HexStringToColor(color).color;
}

// Resolves the normalized height share for the top or bottom panel.
function getPanelHeight(panelRatios: LaneChooserPanelRatios, panelKey: 'top' | 'bottom'): number {
	const safeTop = Math.max(0, panelRatios.top);
	const safeBottom = Math.max(0, panelRatios.bottom);
	const total = Math.max(0.0001, safeTop + safeBottom);

	return panelKey === 'top' ? safeTop / total : safeBottom / total;
}

export abstract class BaseGameScene extends Phaser.Scene {
	// Fixed sky layer that remains viewport-aligned while the portrait playfield pans.
	private skyContainer: Phaser.GameObjects.Container | null = null;
	// Root display container for the active theme world.
	private worldContainer: Phaser.GameObjects.Container | null = null;
	// Overlay container for HUD elements that should stay above world props.
	private hudContainer: Phaser.GameObjects.Container | null = null;
	// Top-most overlay container for transient visuals that must render above both world and HUD.
	private overlayContainer: Phaser.GameObjects.Container | null = null;
	// Fixed instruction layer that must not move with the portrait playfield camera.
	private fixedHudContainer: Phaser.GameObjects.Container | null = null;
	// Latest payload received from React with viewport and lane-count data.
	private currentPayload: LaneChooserWorldPayload | null = null;
	// Shared lane graphics object that can be redrawn without rebuilding the whole scene.
	private laneGraphics: Phaser.GameObjects.Graphics | null = null;
	// Cached lane metrics used by round updates between questions.
	private laneMetrics: LaneShapeMetrics | null = null;
	// Cached viewport width for question/player/answer recreation.
	private viewportWidth = 0;
	// Cached viewport height for question recreation.
	private viewportHeight = 0;
	// Cached X-axis render scale for round-specific controllers.
	private renderScaleX = 1;
	// Cached Y-axis render scale for round-specific controllers.
	private renderScaleY = 1;
	// Current playfield width in Phaser render coordinates.
	private playfieldWidth = 0;
	// Active horizontal camera tween used to follow lane changes on portrait mobile.
	private cameraTween: Phaser.Tweens.Tween | null = null;
	// Cached top lane width percentage used by answer row placement.
	private topWidthPct = 0;
	// Cached bottom lane width percentage used by player layout updates.
	private bottomWidthPct = 0;
	// Dedicated runtime that owns moving bank props for the current world render.
	private sideElementsController: SideElementsController | null = null;
	// Runtime that owns the moving answer-box stream inside the lane.
	private answersController: AnswersController | null = null;
	// Runtime that owns the in-canvas key/swipe hint sprite.
	private instructionController: InstructionController | null = null;
	// Runtime that owns the lane player sprite, lane changes, and temporary correct-state animation.
	private playerController: PlayerController | null = null;
	// Runtime that owns the Phaser DOM question panel overlay.
	private questionController: QuestionController | null = null;
	private lottieSourceRoot: HTMLDivElement | null = null;
	// Tracks whether the forward boost input is currently held.
	private forwardBoostHeld = false;
	// Tracks whether the reverse boost input is currently held.
	private reverseBoostHeld = false;
	// Whether timed gameplay has actually started after the intro instructions and countdown.
	private gameStarted = false;
	private instructionsCompleted = false;
	// Mirrors the established "answers reached the player" state until the active jump has fully landed.
	private answersAtBottom = false;
	// Tracks the previous frame jump state so landing can release the bottom lock.
	private playerWasJumping = false;
	// Runtime that owns the fixed sun and moving cloud sprites.
	private skyController: SkyController | null = null;
	// Theme-provided scene configuration implemented by subclasses.
	protected abstract readonly themeSceneConfig: LaneChooserGameTheme;
	private readonly eventBus: LaneChooserEventBus;

	protected constructor(sceneKey: string, eventBus: LaneChooserEventBus) {
		super(sceneKey);
		this.eventBus = eventBus;
	}

	// Registers scene listeners and waits for React to provide the first world payload.
	create() {
		this.skyContainer = this.add.container(0, 0);
		this.skyContainer.setScrollFactor(0);
		this.worldContainer = this.add.container(0, 0);
		this.hudContainer = this.add.container(0, 0);
		this.overlayContainer = this.add.container(0, 0);
		this.fixedHudContainer = this.add.container(0, 0);
		this.fixedHudContainer.setScrollFactor(0);
		this.lottieSourceRoot = document.createElement('div');
		this.lottieSourceRoot.className = 'lane-chooser__lottie-source-root';
		this.lottieSourceRoot.setAttribute('aria-hidden', 'true');
		document.body.appendChild(this.lottieSourceRoot);

		const handleLoadWorld = (payload: LaneChooserWorldPayload) => {
			if (!this.scene) {
				return;
			}

			this.currentPayload = payload;
			this.renderWorld();
		};
		const handleUpdateRound = (payload: LaneChooserRoundPayload) => {
			if (!this.currentPayload) {
				return;
			}

			this.currentPayload = {
				...this.currentPayload,
				...payload,
			};
			this.renderRound(true);
		};

		const handleGameStarted = (isStarted: boolean) => {
			this.gameStarted = Boolean(isStarted);
			if (this.gameStarted) {
				this.instructionsCompleted = true;
			}
			this.questionController?.setVisible(this.gameStarted);
			this.syncMotionState();
		};
		const handleAnswerResolved = (resolution: LaneChooserAnswerResolution) => {
			this.answersAtBottom = false;
			this.syncMotionState();
			this.answersController?.applyAnswerResolution(resolution);
			if (resolution.isCorrect) {
				this.playerController?.playCorrectAnimation();
			}
		};
		const handleSkipInstructions = () => this.instructionController?.complete();

		this.eventBus.on(EVENTS.LOAD_WORLD, handleLoadWorld);
		this.eventBus.on(EVENTS.UPDATE_ROUND, handleUpdateRound);
		this.eventBus.on(EVENTS.SET_GAME_STARTED, handleGameStarted);
		this.eventBus.on(EVENTS.ANSWER_RESOLVED, handleAnswerResolved);
		this.eventBus.on(EVENTS.SKIP_INSTRUCTIONS, handleSkipInstructions);

		this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
			this.eventBus.off(EVENTS.LOAD_WORLD, handleLoadWorld);
			this.eventBus.off(EVENTS.UPDATE_ROUND, handleUpdateRound);
			this.eventBus.off(EVENTS.SET_GAME_STARTED, handleGameStarted);
			this.eventBus.off(EVENTS.ANSWER_RESOLVED, handleAnswerResolved);
			this.eventBus.off(EVENTS.SKIP_INSTRUCTIONS, handleSkipInstructions);
			this.currentPayload = null;
			this.laneGraphics = null;
			this.laneMetrics = null;
			this.viewportWidth = 0;
			this.viewportHeight = 0;
			this.renderScaleX = 1;
			this.renderScaleY = 1;
			this.playfieldWidth = 0;
			this.cameraTween?.stop();
			this.cameraTween = null;
			this.topWidthPct = 0;
			this.bottomWidthPct = 0;
			this.sideElementsController?.destroy();
			this.sideElementsController = null;
			this.answersController?.destroy();
			this.answersController = null;
			this.instructionController?.destroy();
			this.instructionController = null;
			this.playerController?.destroy();
			this.playerController = null;
			this.questionController?.destroy();
			this.questionController = null;
			this.skyController?.destroy();
			this.skyController = null;
			this.lottieSourceRoot?.remove();
			this.lottieSourceRoot = null;
			this.forwardBoostHeld = false;
			this.reverseBoostHeld = false;
			this.gameStarted = false;
			this.instructionsCompleted = false;
			this.answersAtBottom = false;
			this.playerWasJumping = false;
			this.skyContainer = null;
			this.worldContainer = null;
			this.hudContainer = null;
			this.overlayContainer = null;
			this.fixedHudContainer = null;
		});

		this.time.delayedCall(0, () => this.eventBus.emit(EVENTS.SCENE_READY));
	}

	// Advances frame-based scene systems that are not tween-driven.
	update(_time: number, delta: number) {
		const hasSky = Boolean(this.skyController);
		const hasSideElements = Boolean(this.sideElementsController);
		const hasAnswers = Boolean(this.answersController);
		const hasInstructions = Boolean(this.instructionController);
		const hasPlayer = Boolean(this.playerController);

		if (!hasSky && !hasSideElements && !hasAnswers && !hasInstructions && !hasPlayer) {
			return;
		}

		if (hasSky) {
			this.skyController?.update(delta);
		}

		if (hasSideElements) {
			this.sideElementsController?.update(delta);
		}

		if (hasAnswers) {
			this.answersController?.update(delta);
		}

		if (hasInstructions) {
			this.instructionController?.update();
		}

		const playerIsJumping = this.playerController?.isJumpingNow() ?? false;
		const answersReachedBottom = this.answersController?.isAtBottom() ?? false;

		if (!this.answersAtBottom && answersReachedBottom) {
			this.answersAtBottom = true;
			this.forwardBoostHeld = false;
			this.reverseBoostHeld = false;
			this.syncMotionState();
		}

		if (hasPlayer) {
			this.playerController?.update();
		}

		const playerIsDescending = this.playerController?.isDescendingNow() ?? false;
		if (playerIsDescending || !playerIsJumping) {
			this.answersController?.promoteExitAbovePlayer();
		}

		if (this.answersAtBottom && this.playerWasJumping && !playerIsJumping) {
			this.answersAtBottom = false;
			this.syncMotionState();
		}

		this.playerWasJumping = playerIsJumping;
	}

	// Rebuilds the entire visible world whenever the payload or viewport changes.
	private renderWorld() {
		const mainCamera = this.cameras?.main;
		if (
			!this.worldContainer ||
			!this.hudContainer ||
			!this.overlayContainer ||
			!this.skyContainer ||
			!this.fixedHudContainer ||
			!this.lottieSourceRoot ||
			!this.currentPayload ||
			!mainCamera
		) {
			return;
		}

		this.sideElementsController?.destroy();
		this.sideElementsController = null;
		this.answersController?.destroy();
		this.answersController = null;
		this.playerController?.destroy();
		this.playerController = null;
		this.questionController?.destroy();
		this.questionController = null;
		this.instructionController?.destroy();
		this.instructionController = null;
		this.laneGraphics = null;
		this.laneMetrics = null;
		this.cameraTween?.stop();
		this.cameraTween = null;
		this.skyController?.destroy();
		this.skyController = null;
		this.skyContainer.removeAll(true);
		this.worldContainer.removeAll(true);
		this.hudContainer.removeAll(true);
		this.overlayContainer.removeAll(true);
		this.fixedHudContainer.removeAll(true);

		const renderWidth = this.scale.width;
		const renderHeight = this.scale.height;
		this.viewportWidth = this.currentPayload.viewportWidth || renderWidth;
		this.viewportHeight = this.currentPayload.viewportHeight || renderHeight;
		this.renderScaleX = this.viewportWidth > 0 ? renderWidth / this.viewportWidth : 1;
		this.renderScaleY = this.viewportHeight > 0 ? renderHeight / this.viewportHeight : 1;
		const laneWorldScale = getLaneWorldScale(this.viewportWidth, this.viewportHeight);
		this.playfieldWidth = renderWidth * laneWorldScale;
		const renderScale = Math.min(this.renderScaleX, this.renderScaleY);
		const theme = this.themeSceneConfig;
		const panelRatios =
			this.viewportWidth < SCREEN_SIZES.TABLET ? theme.layout.mobilePanelRatios : theme.layout.desktopPanelRatios;
		const bottomPanelImageHeight =
			(renderWidth * theme.assetSizes.bottomPanel.height) / theme.assetSizes.bottomPanel.width;
		const bottomPanelHeight = renderHeight * getPanelHeight(panelRatios, 'bottom');
		const topPanelHeight = renderHeight * getPanelHeight(panelRatios, 'top');
		const configuredBottomPanelOverlap = bottomPanelImageHeight * Math.max(0, theme.layout.bottomPanelOverlapRatio);
		const portraitMobilePanelOverlap = laneWorldScale > 1 ? renderHeight * PORTRAIT_MOBILE_PANEL_OVERLAP_RATIO : 0;
		const bottomPanelOverlap = Math.max(configuredBottomPanelOverlap, portraitMobilePanelOverlap);
		const bottomPanelTop = Math.max(0, topPanelHeight - bottomPanelOverlap);
		const laneTop = bottomPanelTop;
		const laneHeight = bottomPanelHeight + bottomPanelOverlap;
		this.topWidthPct = getLaneTopPct(this.viewportWidth);
		this.bottomWidthPct = getLaneBottomPct(this.viewportWidth);
		const laneLinesCount = Math.max(
			1,
			Math.min(4, Math.round(this.currentPayload.laneLinesCount || DEFAULT_LANE_LINES_COUNT)),
		);
		this.laneMetrics = createLaneShapeMetrics({
			width: this.playfieldWidth,
			topOffset: laneTop,
			height: laneHeight,
			topWidthPct: this.topWidthPct,
			bottomWidthPct: this.bottomWidthPct,
		});

		mainCamera.setBackgroundColor(getColorValue(theme.colors.background));
		mainCamera.setBounds(0, 0, this.playfieldWidth, renderHeight);
		mainCamera.setScroll(0, 0);

		this.skyController = new SkyController({
			scene: this,
			container: this.skyContainer,
			theme,
			renderWidth,
			renderHeight,
			viewportWidth: this.viewportWidth,
			renderScaleX: this.renderScaleX,
			renderScale,
		});

		const topPanelImage = this.createResponsiveImage(
			theme.textureKeys.topPanel,
			this.playfieldWidth / 2,
			topPanelHeight,
			this.playfieldWidth,
			'bottom',
		);
		if (topPanelImage) {
			this.worldContainer.add(topPanelImage);
		}

		const bottomPanel = this.add.rectangle(
			this.playfieldWidth / 2,
			topPanelHeight + bottomPanelHeight / 2 - bottomPanelOverlap / 2,
			this.playfieldWidth,
			bottomPanelHeight + bottomPanelOverlap,
			getColorValue(theme.colors.bottomPanel),
		);
		this.worldContainer.add(bottomPanel);

		const bottomPanelImage = this.createResponsiveImage(
			theme.textureKeys.bottomPanel,
			this.playfieldWidth / 2,
			bottomPanelTop,
			this.playfieldWidth,
			'top',
		);
		if (bottomPanelImage) {
			this.worldContainer.add(bottomPanelImage);
		}

		if (theme.foreground) {
			const foregroundImage = this.createResponsiveImage(
				theme.foreground.textureKey,
				this.playfieldWidth / 2,
				(renderHeight * theme.foreground.yPct) / 100,
				this.playfieldWidth * (theme.foreground.widthScale ?? 1),
				'center',
			);
			if (foregroundImage) {
				this.worldContainer.add(foregroundImage);
			}
		}

		this.laneGraphics = this.add.graphics();
		this.redrawLaneGraphics(laneLinesCount);
		this.worldContainer.add(this.laneGraphics);

		this.sideElementsController = new SideElementsController({
			scene: this,
			container: this.worldContainer,
			laneMetrics: this.laneMetrics,
			renderScaleX: this.renderScaleX,
			renderScaleY: this.renderScaleY,
			viewportWidth: this.viewportWidth,
			themeConfig: theme.sideElements,
		});

		this.playerController = new PlayerController({
			scene: this,
			container: this.hudContainer,
			laneMetrics: this.laneMetrics,
			laneLinesCount,
			laneBottomPct: this.bottomWidthPct,
			viewportWidth: this.viewportWidth,
			renderScaleX: this.renderScaleX,
			renderScaleY: this.renderScaleY,
			themeConfig: theme.player,
			answerBoxWidths: theme.answers.widthPx,
			lottieSourceRoot: this.lottieSourceRoot,
		});

		this.instructionController = new InstructionController({
			scene: this,
			container: this.fixedHudContainer,
			viewportWidth: this.viewportWidth,
			renderScaleX: this.renderScaleX,
			renderScaleY: this.renderScaleY,
			layout: theme.instructions,
			lottieSourceRoot: this.lottieSourceRoot,
			showInstructions: !this.instructionsCompleted,
			onMoveLeft: () => {
				if (this.playerController?.moveLeft()) {
					this.eventBus.emit(EVENTS.SOUND_EFFECT, 'switch-lane-left');
					this.syncCameraToPlayer(true);
				}
			},
			onMoveRight: () => {
				if (this.playerController?.moveRight()) {
					this.eventBus.emit(EVENTS.SOUND_EFFECT, 'switch-lane-right');
					this.syncCameraToPlayer(true);
				}
			},
			onJump: () => this.handleJumpAction(),
			onStartForwardBoost: () => this.startForwardBoost(),
			onStopForwardBoost: () => this.stopForwardBoost(),
			onStartReverseBoost: () => this.startReverseBoost(),
			onStopReverseBoost: () => this.stopReverseBoost(),
			onInstructionComplete: () => {
				this.instructionsCompleted = true;
				this.eventBus.emit(EVENTS.INSTRUCTION_COMPLETE);
			},
			onTooltipChange: (textKey, point) => {
				this.eventBus.emit(EVENTS.INSTRUCTION_TOOLTIP, textKey);
				this.eventBus.emit(EVENTS.INSTRUCTION_TOOLTIP_POS, point);
			},
		});
		this.questionController = new QuestionController({
			scene: this,
			question: null,
			viewportWidth: this.viewportWidth,
			viewportHeight: this.viewportHeight,
			onImagePreview: (imageSrc) => this.eventBus.emit(EVENTS.QUESTION_IMAGE_PREVIEW, imageSrc),
		});

		this.renderRound(false);
		this.syncMotionState();
	}

	// Rebuilds only the question-specific layer so the static world can keep running between rounds.
	private renderRound(animateQuestion: boolean) {
		if (
			!this.currentPayload ||
			!this.worldContainer ||
			!this.overlayContainer ||
			!this.lottieSourceRoot ||
			!this.laneGraphics ||
			!this.laneMetrics ||
			!this.questionController
		) {
			return;
		}

		const laneLinesCount = Math.max(
			1,
			Math.min(4, Math.round(this.currentPayload.laneLinesCount || DEFAULT_LANE_LINES_COUNT)),
		);

		this.redrawLaneGraphics(laneLinesCount);
		this.answersController?.destroy();
		this.answersAtBottom = false;
		this.answersController = new AnswersController({
			scene: this,
			container: this.worldContainer,
			exitContainer: this.overlayContainer,
			laneDisplayObject: this.laneGraphics,
			laneMetrics: this.laneMetrics,
			laneLinesCount,
			topWidthPct: this.topWidthPct,
			viewportWidth: this.viewportWidth,
			renderScaleX: this.renderScaleX,
			renderScaleY: this.renderScaleY,
			question: this.currentPayload.question,
			themeConfig: this.themeSceneConfig.answers,
			playerThemeConfig: this.themeSceneConfig.player,
			lottieSourceRoot: this.lottieSourceRoot,
			onRoundExitComplete: () => this.eventBus.emit(EVENTS.ROUND_EXIT_COMPLETE),
		});
		this.playerController?.setLaneLayout({
			laneMetrics: this.laneMetrics,
			laneLinesCount,
			laneBottomPct: this.bottomWidthPct,
			viewportWidth: this.viewportWidth,
			renderScaleX: this.renderScaleX,
			renderScaleY: this.renderScaleY,
		});
		this.playerWasJumping = this.playerController?.isJumpingNow() ?? false;
		this.questionController.setQuestion(this.currentPayload.question, animateQuestion);
		this.questionController.setVisible(this.gameStarted);
		this.syncCameraToPlayer(false);
		this.syncMotionState();
	}

	// Keeps the selected portrait-mobile lane in view while preserving the established bounded edge behavior.
	private syncCameraToPlayer(animate: boolean) {
		const mainCamera = this.cameras?.main;
		const targetX = this.playerController?.getLaneCenterX();
		if (!mainCamera || targetX == null) {
			return;
		}

		const targetScrollX = getLaneCameraScrollX({
			targetX,
			worldWidth: this.playfieldWidth,
			viewportWidth: mainCamera.width,
		});

		this.cameraTween?.stop();
		this.cameraTween = null;

		if (!animate || Math.abs(mainCamera.scrollX - targetScrollX) <= 0.5) {
			mainCamera.setScroll(targetScrollX, 0);
			return;
		}

		this.cameraTween = this.tweens.add({
			targets: mainCamera,
			scrollX: targetScrollX,
			duration: this.themeSceneConfig.player.laneSwitchDurationMs ?? 220,
			ease: 'Power1',
			onComplete: () => {
				this.cameraTween = null;
			},
		});
	}

	// Redraws the lane fill and divider count without recreating the whole scene graph.
	private redrawLaneGraphics(laneLinesCount: number) {
		if (!this.laneGraphics || !this.laneMetrics) {
			return;
		}

		drawLane({
			graphics: this.laneGraphics,
			metrics: this.laneMetrics,
			laneLinesCount,
			bottomLineWidth:
				this.viewportWidth <= SCREEN_SIZES.MOBILE
					? MOBILE_LANE_LINE_BOTTOM_WIDTH
					: DESKTOP_LANE_LINE_BOTTOM_WIDTH,
			laneColor: getColorValue(this.themeSceneConfig.colors.lane),
			laneLineColor: getColorValue(this.themeSceneConfig.colors.laneLine),
		});
	}

	// Applies one shared motion state to every runtime that reacts to speed or direction.
	private applyMotion(motionScale: number, motionDirection: 1 | -1) {
		const playerIsJumping = this.playerController?.isJumpingNow() ?? false;
		const sideElementsPaused = this.answersAtBottom && !playerIsJumping;
		const isBoosting = !this.answersAtBottom && motionScale > this.themeSceneConfig.motion.normalScale;
		const sideElementsMotionScale = sideElementsPaused
			? 0
			: isBoosting
			? this.themeSceneConfig.sideElements.boostMotionScale ?? this.themeSceneConfig.sideElements.motionScale ?? 1
			: this.themeSceneConfig.sideElements.motionScale ?? 1;
		const answersMotionScale = this.gameStarted && !this.answersAtBottom ? motionScale : 0;

		this.sideElementsController?.setMotion(sideElementsMotionScale, motionDirection);
		this.answersController?.setMotion(answersMotionScale, motionDirection);
		this.answersController?.setWaitingAtBottom(this.answersAtBottom);
		this.playerController?.setWaitingAtBottom(this.answersAtBottom);
		this.playerController?.setBoosting(isBoosting);
	}

	// Jumps the player and, when the answer row is ready, submits the current lane as the chosen answer.
	private handleJumpAction() {
		this.eventBus.emit(EVENTS.SOUND_EFFECT, 'jump');
		const didJump = this.playerController?.jump();
		if (didJump) {
			this.syncMotionState();
		}

		if (!didJump || !this.answersController?.beginAnswerRequest()) {
			return;
		}

		this.eventBus.emit(EVENTS.ANSWER_REQUESTED, {
			selectedIndex: this.playerController?.getLaneIndex() ?? 0,
		});
	}

	// Reapplies the active boost state after scene rerenders such as resize or payload changes.
	private syncMotionState() {
		if (this.forwardBoostHeld) {
			this.applyMotion(this.themeSceneConfig.motion.boostScale, 1);
			return;
		}

		if (this.reverseBoostHeld) {
			this.applyMotion(this.themeSceneConfig.motion.boostScale, -1);
			return;
		}

		this.applyMotion(this.themeSceneConfig.motion.normalScale, 1);
	}

	// Starts the established forward boost state and keeps it active until its key or swipe is released.
	private startForwardBoost() {
		if (this.answersAtBottom || this.forwardBoostHeld) {
			return;
		}

		this.forwardBoostHeld = true;
		this.eventBus.emit(EVENTS.SOUND_EFFECT, 'boost-start');
		this.applyMotion(this.themeSceneConfig.motion.boostScale, 1);
	}

	// Returns from forward boost to either reverse boost or the default downstream motion.
	private stopForwardBoost() {
		if (!this.forwardBoostHeld) {
			return;
		}

		this.forwardBoostHeld = false;
		this.eventBus.emit(EVENTS.SOUND_EFFECT, 'boost-stop');

		if (this.reverseBoostHeld) {
			this.applyMotion(this.themeSceneConfig.motion.boostScale, -1);
			return;
		}

		this.applyMotion(this.themeSceneConfig.motion.normalScale, 1);
	}

	// Starts the established reverse boost state and keeps it active until its key or swipe is released.
	private startReverseBoost() {
		if (this.answersAtBottom || this.reverseBoostHeld) {
			return;
		}

		this.reverseBoostHeld = true;
		this.eventBus.emit(EVENTS.SOUND_EFFECT, 'slowdown-start');
		this.applyMotion(this.themeSceneConfig.motion.boostScale, -1);
	}

	// Returns from reverse boost to either forward boost or the default downstream motion.
	private stopReverseBoost() {
		if (!this.reverseBoostHeld) {
			return;
		}

		this.reverseBoostHeld = false;
		this.eventBus.emit(EVENTS.SOUND_EFFECT, 'slowdown-stop');

		if (this.forwardBoostHeld) {
			this.applyMotion(this.themeSceneConfig.motion.boostScale, 1);
			return;
		}

		this.applyMotion(this.themeSceneConfig.motion.normalScale, 1);
	}

	// Builds a full-width responsive image used by the top and bottom panel artwork.
	private createResponsiveImage(
		textureKey: string,
		x: number,
		y: number,
		displayWidth: number,
		anchor: ResponsiveImageAnchor,
	): Phaser.GameObjects.Image | null {
		const texture = this.textures.get(textureKey);
		if (!texture || !texture.getSourceImage()) {
			return null;
		}

		const sourceImage = texture.getSourceImage() as {
			width: number;
			height: number;
		};
		if (!sourceImage.width || !sourceImage.height) {
			return null;
		}

		const image = this.add.image(x, y, textureKey);
		const imageScale = displayWidth / sourceImage.width;
		image.setOrigin(0.5, anchor === 'bottom' ? 1 : anchor === 'center' ? 0.5 : 0);
		image.setDisplaySize(displayWidth, sourceImage.height * imageScale);

		return image;
	}
}
